import type {
  ChatbotConfig,
  ChatMessageType,
  ChatUserType,
  FeedbackType,
  ThreadType,
  FAQType,
  OrganizationWorkingHours,
} from '@form-factory/schema';
import {
  type ChatThemeConfig,
  efCreateMemberSchema,
  efOnlinePaymentSchema,
  efOnlinePaymentResponseSchema,
  efAuthSchema,
  withClubId,
  efIntAuthResponseSchema,
  efIntRefreshResponseSchema,
  efRefreshSchema,
  efIntConsentDefinitionsResponseSchema,
  efIntMembershipDefinitionsResponseSchema,
  efIntCreateMemberResponseSchema,
  efCreateMembershipResponseSchema,
  efIntCreateMembershipSchema,
  takeoverRequestSchema,
  efIntGroupClassesResponseSchema,
  efGetGroupClassesParamsSchema,
  efGroupClassDetailResponseSchema,
  efCreateClassReservationSchema,
  efCreateClassReservationResponseSchema,
  efGetMemberClassReservationsResponseSchema,
  efGetMemberClassReservationsParamsSchema,
  createChatMessageSchema,
} from '@form-factory/schema/contracts';
import {
  Language,
  FeedbackCategory,
  type Rating,
  PauseMembershipReasons,
  ThreadMode,
} from '@form-factory/schema/enums';
import { createTypedKyClient, type ApiDefinition } from '@form-factory/shared';
import ky, { type KyResponse } from '@toss/ky';
import { z } from 'zod';

import { AppSettings } from '@/constants/app-settings';
import { getCurrentLocale } from '@/locale/i18n';
import { useBoundStore } from '@/store/store';

/**
 * Converts the language to the language code used by the AI Core API.
 */
export function getLanguageCode(language: string | undefined) {
  switch (language) {
    case Language.Croatian:
      return 'hr-HR';
    case Language.Slovak:
      return 'sk-SK';
    case Language.Czech:
      return 'cs-CZ';
    case Language.English:
      return 'en-US';

    default:
      return 'en-US';
  }
}

// Export the ErrorResponse type
export type ErrorResponse = {
  statusCode: number;
  error: string;
  message: string;
};

// Define the API schema
const aiCoreApiDefinition = {
  auth: {
    local: {
      method: 'POST',
      path: 'chats/:chatId/auth/local',
      input: z.object({
        chatId: z.string(),
      }),
      output: z.object({
        user: z.custom<ChatUserType>(),
        token: z.string(),
      }),
    },
    verify: {
      method: 'GET',
      path: 'chats/:chatId/auth/verify',
      input: z.object({
        chatId: z.string(),
      }),
      output: z.object({
        valid: z.boolean(),
      }),
    },
    resend: {
      method: 'POST',
      path: 'chats/auth/register/resend',
      input: z.object({
        email: z.string().email(),
      }),
    },
  },
  threads: {
    findOne: {
      method: 'GET',
      path: 'chats/:chatId/threads/:id',
      input: z.object({
        chatId: z.string(),
        id: z.string(),
      }),
      output: z.union([
        z.custom<
          Omit<ThreadType, 'messages'> & { messages: ChatMessageType[] }
        >(),
        z.null(),
      ]),
    },
    findAll: {
      method: 'GET',
      path: 'chats/:chatId/threads',
      input: z.object({
        chatId: z.string(),
      }),
      output: z.array(z.custom<ThreadType>()),
    },
    create: {
      method: 'POST',
      path: 'chats/:chatId/threads',
      input: z.object({
        chatId: z.string(),
      }),
      output: z.custom<ThreadType>(),
    },
    // TODO put schemas in shared contract
    ping: {
      method: 'POST',
      path: 'chats/:chatId/threads/:threadId/ping',
      input: z.object({
        chatId: z.string(),
        threadId: z.string(),
        mode: z.nativeEnum(ThreadMode).nullish(),
      }),
      output: z.object({
        takeover: takeoverRequestSchema,
      }),
    },
    respond: {
      method: 'POST',
      path: 'chats/:chatId/threads/:threadId/takeover/respond',
      input: z.object({
        chatId: z.string(),
        threadId: z.string(),
        accepted: z.boolean(),
      }),
      output: z.void(),
    },
    messages: {
      create: {
        method: 'POST',
        path: 'chats/:chatId/threads/:threadId/messages',
        input: z.object({
          chatId: z.string(),
          threadId: z.string(),
          messages: z.array(createChatMessageSchema.omit({ thread: true })),
        }),
        output: z.void(),
      },
    },
  },
  chat: {
    completion: {
      method: 'POST',
      path: 'chats/:chatId/threads/:threadId/completion',
      input: z.object({
        chatId: z.string(),
        threadId: z.string(),
        message: z.string(),
      }),
      output: z.custom<KyResponse>(),
    },
    config: {
      method: 'GET',
      path: 'chats/:chatId/config',
      input: z.object({
        chatId: z.string(),
      }),
      output: z.object({
        agentName: z.string(),
        chatbotConfig: z.custom<ChatbotConfig>(),
        themeConfig: z.custom<ChatThemeConfig>(),
        workingHours: z.custom<OrganizationWorkingHours>(),
      }),
    },
  },
  feedback: {
    create: {
      method: 'POST',
      path: 'messages/:messageId/feedback',
      input: z.object({
        messageId: z.string(),
        rating: z.custom<Rating>(),
        note: z.string().nullish(),
        category: z.nativeEnum(FeedbackCategory).nullish(),
      }),
      output: z.custom<FeedbackType>(),
    },
  },
  faqs: {
    findAll: {
      method: 'GET',
      path: 'chats/:chatId/faqs',
      input: z.object({
        chatId: z.string(),
        parentId: z.string().uuid().nullish(),
      }),
      output: z.array(z.custom<FAQType>()),
    },
    increment: {
      method: 'GET',
      path: 'chats/:chatId/faqs/:faqId/increment',
      input: z.object({
        chatId: z.string(),
        faqId: z.string(),
      }),
      output: z.void(),
    },
  },
  eFitness: {
    pauseMembership: {
      method: 'POST',
      path: 'efitness/members/memberships/pause',
      input: z.object({
        chatId: z.string().uuid(),
        reason: z.nativeEnum(PauseMembershipReasons),
        monthDuration: z.string().optional(),
        note: z.string().optional(),
        file: z.string().optional(),
        fileName: z.string().optional(),
      }),
    },
    auth: {
      login: {
        method: 'POST',
        path: 'efitness/clubs/:clubId/auth',
        input: withClubId(efAuthSchema),
        output: efIntAuthResponseSchema,
      },
      refresh: {
        method: 'POST',
        path: 'efitness/clubs/:clubId/auth/refresh',
        input: withClubId(efRefreshSchema),
        output: efIntRefreshResponseSchema,
      },
    },
    members: {
      create: {
        method: 'POST',
        path: 'efitness/clubs/:clubId/member',
        input: withClubId(efCreateMemberSchema),
        output: efIntCreateMemberResponseSchema,
      },
    },
    payments: {
      onlinePayment: {
        method: 'POST',
        path: 'efitness/clubs/:clubId/members/payments/online-payments/transactions',
        input: withClubId(efOnlinePaymentSchema),
        output: efOnlinePaymentResponseSchema,
      },
    },
    membershipDefinitions: {
      memberships: {
        method: 'GET',
        path: 'efitness/clubs/:clubId/membership-definitions',
        input: withClubId(),
        output: efIntMembershipDefinitionsResponseSchema,
      },
      consents: {
        method: 'GET',
        path: 'efitness/clubs/:clubId/membership-definitions/:id/consent-definitions',
        input: withClubId(
          z.object({
            id: z.number(),
          }),
        ),
        output: efIntConsentDefinitionsResponseSchema,
      },
    },
    memberships: {
      create: {
        method: 'POST',
        path: 'efitness/clubs/:clubId/members/memberships',
        input: withClubId(efIntCreateMembershipSchema),
        output: efCreateMembershipResponseSchema,
      },
    },
    clubs: {
      findAll: {
        method: 'GET',
        path: 'clubs',
        input: z.object({
          chatId: z.string(),
        }),
      },
    },
    groupClasses: {
      findAll: {
        method: 'GET',
        path: 'efitness/clubs/:clubId/classes',
        input: withClubId(efGetGroupClassesParamsSchema),
        output: efIntGroupClassesResponseSchema,
      },
      findOne: {
        method: 'GET',
        path: 'efitness/clubs/:clubId/classes/:classId',
        input: withClubId(z.object({ classId: z.string() })),
        output: efGroupClassDetailResponseSchema,
      },
      findAllReservations: {
        method: 'GET',
        path: 'efitness/clubs/:clubId/reservations',
        input: withClubId(efGetMemberClassReservationsParamsSchema),
        output: efGetMemberClassReservationsResponseSchema,
      },
      createReservation: {
        method: 'POST',
        path: 'efitness/clubs/:clubId/reservations',
        input: withClubId(efCreateClassReservationSchema),
        output: efCreateClassReservationResponseSchema,
      },
      cancelReservation: {
        method: 'DELETE',
        path: 'efitness/clubs/:clubId/reservations/:id',
        input: withClubId(
          z.object({
            id: z.string(),
          }),
        ),
        output: z.void(),
      },
    },
  },
} as const satisfies ApiDefinition;

/**
 * Ky client instance, with the base URL, other configs
 * and authentication headers.
 */
const kyInstance = ky.create({
  prefixUrl: AppSettings.apiUrl,
  throwHttpErrors: true,
  hooks: {
    beforeRequest: [
      request => {
        const { accessToken } = useBoundStore.getState();

        if (!accessToken) {
          return request;
        }

        request.headers.set(
          'accept-language',
          getLanguageCode(getCurrentLocale()),
        );
        request.headers.set('Authorization', `Bearer ${accessToken}`);

        return request;
      },
    ],
    beforeError: [
      error => {
        if (import.meta.env.DEV) {
          console.error(JSON.stringify(error, null, 2));
        }

        return error;
      },
    ],
    afterResponse: [
      async (request, _, response) => {
        /**
         * Makes sure the user is logged out properly
         * if the token is invalid.
         */
        // Only act on 401 for requests that included an Authorization header
        const hadAuthHeader = request.headers.get('Authorization');
        if (
          typeof response === 'object' &&
          'status' in response &&
          response.status === 401 &&
          !request.url.includes('/auth') &&
          hadAuthHeader
        ) {
          useBoundStore.getState().logout();
          useBoundStore.getState().close();
        }

        return response;
      },
    ],
  },
});

/**
 * Typed Ky client for the AI Core API
 */
export const aiCoreApi = createTypedKyClient({
  client: kyInstance,
  apiDefinition: aiCoreApiDefinition,
});
